import Reveal from "@/components/site/Reveal";
import PageHero from "@/components/site/PageHero";
import LeadForm from "@/components/site/LeadForm";
import Seo, { ORG_SCHEMA, serviceSchema, breadcrumbSchema } from "@/components/site/Seo";

const SERVICES = [
  { t: "Housing Search", d: "Curated viewings across Dubai's most prestigious communities and apartments." },
  { t: "School Placement", d: "Direct relationships with leading British and international schools." },
  { t: "Banking Introductions", d: "Personal and private banking introductions to UAE and international banks." },
  { t: "Healthcare", d: "Insurance, GP and specialist registration tailored to your family's needs." },
  { t: "Vehicle & Drivers", d: "Purchase, lease, registration and trusted drivers — arranged before arrival." },
  { t: "Lifestyle Curation", d: "Clubs, gyms, restaurants, household staff and the small details that matter." },
];

export default function Relocation() {
  return (
    <div className="bg-white">
      <Seo
        title="UAE Relocation — Housing, Schools & Banking"
        description="Concierge UAE relocation — housing, school placement, banking, healthcare and lifestyle setup, managed by one dedicated British consultancy."
        keywords="UAE relocation planning, moving to Dubai from UK, Dubai school placement, UAE banking for expats, Dubai housing search, UAE relocation concierge, property investment Dubai"
        focusKeyword="UAE relocation planning"
        path="/relocation"
        schema={[
          ORG_SCHEMA,
          serviceSchema({
            name: "UAE Concierge Relocation",
            description:
              "End-to-end relocation planning to the UAE — housing search, school placement, banking, healthcare, vehicles and lifestyle curation, managed by one dedicated advisor.",
            path: "/relocation",
            serviceType: "Relocation and concierge services",
            offers: {
              "@type": "OfferCatalog",
              name: "Relocation Services",
              itemListElement: SERVICES.map((s) => ({
                "@type": "Offer",
                itemOffered: { "@type": "Service", name: s.t, description: s.d },
              })),
            },
          }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Relocation", path: "/relocation" },
          ]),
        ]}
      />
      <PageHero
        overline="Relocation Planning"
        title="Your next chapter starts here."
        subtitle="From the moment you decide to move, until your family feels at home — handled by one dedicated consultancy."
        image="https://images.pexels.com/photos/19612315/pexels-photo-19612315.jpeg?auto=compress&cs=tinysrgb&w=1650&fm=webp"
        imageAlt="Dubai Marina waterfront at dusk — representing concierge UAE relocation and lifestyle setup"
      />

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="text-[11px] tracking-overline uppercase text-black/45">Philosophy</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.05]">A transition, <em className="italic">thoughtfully managed</em>.</h2>
          </Reveal>
          <Reveal delay={120} className="md:col-span-7 text-black/70 font-light text-lg leading-relaxed space-y-5">
            <p>Relocation isn't paperwork — it's a thousand small decisions, made under pressure, in an unfamiliar city. Velor's concierge service makes them on your behalf, with your priorities at the centre.</p>
            <p>You'll work with one senior advisor from briefing to handover. We coordinate every supplier, schedule every viewing, and make sure your first week in Dubai feels familiar from day one.</p>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-black/10 bg-[#fafafa] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <div className="text-[11px] tracking-overline uppercase text-black/45">Service tiers</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4">Tailored to the depth of help you need.</h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-black/10">
            {SERVICES.map((p, i) => (
              <Reveal key={p.t} delay={(i % 3) * 80} className="border-r border-b border-black/10 p-8 md:p-10 bg-white">
                <div className="text-[11px] tracking-overline uppercase text-black/45">0{i+1}</div>
                <h3 className="font-serif text-3xl mt-4">{p.t}</h3>
                <p className="mt-3 text-black/60 font-light leading-relaxed">{p.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <div className="text-[11px] tracking-overline uppercase text-black/45">Timeline</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 max-w-2xl">From first call to first morning coffee.</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-10">
            {[
              { n: "Week 0", t: "Discovery", d: "Confidential briefing covering family, work, schools and lifestyle." },
              { n: "Week 1-2", t: "Visas & licences", d: "Residency and Golden Visa applications submitted in parallel." },
              { n: "Week 2-4", t: "Pre-arrival setup", d: "Housing shortlist, schools, banking, healthcare." },
              { n: "Week 4+", t: "Arrival & beyond", d: "Move-in, ongoing PRO and lifestyle support." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 80}>
                <div className="text-[11px] tracking-overline uppercase text-black/45">{s.n}</div>
                <h3 className="font-serif text-2xl mt-3">{s.t}</h3>
                <p className="mt-3 text-black/60 font-light">{s.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0a0a0a] text-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="text-[11px] tracking-overline uppercase text-white/45">Begin</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.05]">Plan your move <em className="italic">quietly</em>.</h2>
            <p className="mt-6 text-white/65 font-light max-w-md">Share the basics — family, timeline, priorities — and we'll build a private plan.</p>
          </Reveal>
          <Reveal delay={120} className="md:col-span-7">
            <div className="bg-white text-black"><LeadForm testid="relocation-lead-form" source="relocation" /></div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
